import getSuggestedAddresses from '@rsos/reduxDataStore/src/features/gisSearch/helpers';
import {
  fetchPOICandidates,
  resolveSuggestions,
  type SuggestEntry,
  type ThunkDispatch,
} from '../../GlobalMapSearch.poi';
import type { SearchResultItem } from '../../GlobalMapSearch.types';
import { normalizePlaceCoords } from '../../GlobalMapSearch.utils';

interface RunGeocodeSearchArgs {
  text: string;
  lat: number;
  lng: number;
  serviceUrl?: string;
  token?: string | null;
  signal: AbortSignal;
  collected: SearchResultItem[];
  flush: () => void;
  thunkDispatch: ThunkDispatch;
}

/**
 * Address + POI lookup for the current map center. Local POIs come back from
 * the tenant geocoder; ESRI suggest fills the rest. Already-collected
 * addresses are deduped by normalized address string.
 */
export async function runGeocodeSearch({
  text,
  lat,
  lng,
  serviceUrl,
  token,
  signal,
  collected,
  flush,
  thunkDispatch,
}: RunGeocodeSearchArgs): Promise<void> {
  try {
    if (signal.aborted) return;

    const [suggestResult, poiCandidates] = await Promise.all([
      thunkDispatch(
        getSuggestedAddresses?.initiate({
          searchTerm: text.toLowerCase(),
          latitude: lat,
          longitude: lng,
          serviceUrl,
          token,
        }),
      ),
      fetchPOICandidates({
        text,
        lng,
        lat,
        serviceUrl,
        token,
        signal,
      }),
    ]);

    if (signal.aborted) return;

    const seen = new Set<string>();

    for (const raw of poiCandidates) {
      if (!raw.address || !raw.location) continue;
      const candidate = normalizePlaceCoords(raw);
      seen.add(candidate.address.toLowerCase());
      collected.push({ type: 'place', data: candidate });
      flush();
    }

    const suggestions = (suggestResult?.data as SuggestEntry[]) || [];

    const resolved = await resolveSuggestions({
      suggestions,
      seen,
      dispatch: thunkDispatch,
      signal,
    });
    for (const r of resolved) {
      collected.push({ type: 'place', data: r });
      flush();
    }
  } catch {
    // Geocode search failed — leave already-collected results in place.
  }
}
